import React from 'react';
import { Link } from 'react-router-dom';
import homeImg from '../assets/howToUse/home.png';
import searchPageImg from '../assets/howToUse/search-page.png';
import productPageImg from '../assets/howToUse/product-page.png';
import confirmOrderImg from '../assets/howToUse/confirm-order-page.png';

const WHATSAPP_COMMUNITY_URL = 'https://chat.whatsapp.com/HOwqPNZZATxALHZ2MGN8b8?mode=gi_t';
const WHATSAPP_DIRECT_URL = 'https://wa.me/2349067409148';

const processSteps = [
  {
    step: '01',
    title: 'Find What You Want',
    description: 'Search for products using keywords, product photos or a 1688 product link.',
  },
  {
    step: '02',
    title: 'Place Your Order',
    description: 'Review the product and place your order through Checkstall without handling China-side purchasing yourself.',
  },
  {
    step: '03',
    title: 'We Receive and Inspect in China',
    description: 'Your goods arrive at our China warehouse, where they can be checked and prepared for shipment.',
  },
  {
    step: '04',
    title: 'Ship to Nigeria',
    description: 'Choose an available shipping option and have your goods shipped from China to Nigeria.',
  },
];

const benefits = [
  'Source products from Chinese suppliers',
  'Search using 1688 product links',
  'Pay conveniently through Checkstall',
  'RMB trade settlement for supplier payments',
  'China-side receiving and inspection',
  'Shipping support to Nigeria',
];

const appScreens = [
  {
    image: homeImg,
    title: 'Search from your phone',
    alt: 'Checkstall app home screen',
  },
  {
    image: searchPageImg,
    title: 'Find China products',
    alt: 'Checkstall product search screen',
  },
  {
    image: productPageImg,
    title: 'Review product details',
    alt: 'Checkstall product details screen',
  },
  {
    image: confirmOrderImg,
    title: 'Confirm before payment',
    alt: 'Checkstall order confirmation screen',
  },
];

const warehouseVideos = [
  {
    title: 'Negotiating and Securing Customer Deals',
    description: 'See how we negotiate with suppliers and secure deals on behalf of our customers.',
    youtubeId: 'I5LJ9nTiG-0',
  },
  {
    title: 'Inspection and packing',
    description: 'A look at goods being checked and prepared before shipment.',
    youtubeId: '40shymgXoAQ',
  },
  {
    title: 'Receiving and Packaging Customer Goods',
    description: 'See customers’ goods arriving at our warehouse, being received and carefully packaged for shipment.',
    youtubeId: '9GnKsQUPj6A',
  },
  {
    title: 'Arrived in Nigeria',
    description: "See us collecting customers' goods from our shipping partner after arrival in Nigeria.",
    youtubeId: 'XdL3S5aJc3o',
  },
];

const testimonialScreenshots = [
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-1.jpg',
    alt: 'Customer testimonial screenshot 1',
  },
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-2.jpg',
    alt: 'Customer testimonial screenshot 2',
  },
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-3.jpg',
    alt: 'Customer testimonial screenshot 3',
  },
];

const HowItWorks = () => {
  return (
    <main className="min-h-screen bg-white text-gray-950">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-gray-50">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-9 px-4 py-10 sm:px-6 md:py-14 lg:grid-cols-2 lg:px-8">
          <div>
            <div data-section-view="hero_view">
              <p className="mb-3 inline-flex rounded-full bg-primary-100 px-4 py-2 text-sm font-bold text-primary-700">
                Checkstall China Import
              </p>
              <h1 className="text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
                Buy Directly From China &amp; 1688 — We Handle the Rest
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl">
                Search products, pay suppliers, inspect your goods in China and ship to Nigeria with Checkstall.
              </p>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/downloads"
                data-cta-location="hero"
                className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-primary px-7 py-4 text-base font-bold text-white shadow-soft-lg transition-colors hover:bg-primary-600"
              >
                Download Checkstall
              </Link>
              <a
                href={WHATSAPP_DIRECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-cta-location="hero"
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-green-600 px-7 py-4 text-base font-bold text-white shadow-soft transition-colors hover:bg-green-700"
              >
                Chat With Us on WhatsApp
              </a>
            </div>
            <p className="mt-5 text-sm font-semibold leading-6 text-gray-600">
              China sourcing <span aria-hidden="true">•</span> Supplier payment <span aria-hidden="true">•</span> Inspection <span aria-hidden="true">•</span> Shipping to Nigeria
            </p>
          </div>

          <div className="mx-auto w-full max-w-xl">
            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-soft-lg">
              <div className="grid grid-cols-2 gap-3">
                {appScreens.map((screen) => (
                  <div key={screen.title} className="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                    <img
                      src={screen.image}
                      alt={screen.alt}
                    className="h-44 w-full object-cover object-top sm:h-52 lg:h-48 xl:h-52"
                      loading="lazy"
                    />
                    <p className="px-3 py-3 text-sm font-semibold text-gray-800">{screen.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div data-section-view="operations_view" className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Real operations</p>
              <h2 className="text-3xl font-black sm:text-4xl">See How We Handle Your Goods</h2>
              <p className="mt-3 text-lg leading-8 text-gray-600">
                Follow real customer goods from China-side receiving and inspection through to arrival in Nigeria.
              </p>
          </div>
            <div className="-mx-4 mt-7 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0 md:grid md:grid-cols-2 md:overflow-visible md:pb-0 xl:grid-cols-4">
              {warehouseVideos.map((item) => (
                <article key={item.title} className="w-[78vw] max-w-[310px] flex-none snap-center overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-soft md:w-auto md:max-w-none">
                  <div className="aspect-[9/16] bg-gray-950">
                    {item.youtubeId ? (
                      <iframe
                        className="h-full w-full"
                        src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?rel=0`}
                        title={item.title}
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    ) : (
                      <video className="h-full w-full object-cover" controls playsInline preload="none" title={item.title}>
                        <source src={item.src} type="video/mp4" />
                      </video>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600">{item.description}</p>
                  </div>
                </article>
              ))}
            </div>
            <p className="mt-3 text-center text-sm text-gray-500 md:hidden">Swipe to see the full journey →</p>
        </div>
      </section>

      <section id="how-checkstall-works" className="bg-gray-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div data-section-view="how_it_works_view" className="mx-auto mb-8 max-w-3xl text-center">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Simple process</p>
            <h2 className="text-3xl font-black sm:text-4xl">How Checkstall Works</h2>
            <p className="mt-4 text-lg text-gray-600">
              A short path from product discovery to goods leaving China.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((item) => (
              <div key={item.step} className="rounded-2xl bg-white p-5 shadow-soft">
                <div className="mb-3 text-3xl font-black text-primary">{item.step}</div>
                <h3 className="text-xl font-bold text-gray-950">{item.title}</h3>
                <p className="mt-3 leading-7 text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-7 rounded-2xl bg-white p-5 text-center shadow-soft">
            <p className="text-lg font-semibold text-gray-900">Need the full step-by-step guide?</p>
            <Link
              to="/how-to-use"
              data-cta-location="process"
              className="mt-4 inline-flex min-h-12 items-center justify-center rounded-2xl border-2 border-primary px-6 py-3 font-bold text-primary transition-colors hover:bg-primary hover:text-white"
            >
              View How to Use Checkstall
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div data-section-view="customer_proof_view" className="mx-auto max-w-3xl text-center">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Customer proof</p>
            <h2 className="text-3xl font-black sm:text-4xl">Trusted by Importers</h2>
            <p className="mt-3 text-lg leading-8 text-gray-600">
              Real screenshots from customer conversations and feedback after using Checkstall.
            </p>
          </div>
          <div className="-mx-4 mt-7 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0 md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
            {testimonialScreenshots.map((item, index) => (
              <article key={item.src} className="w-[82vw] max-w-sm flex-none snap-center overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-soft md:w-auto md:max-w-none">
                <img src={item.src} alt={item.alt} className="h-auto w-full object-contain" loading="lazy" />
                <div className="p-3">
                  <p className="text-sm font-semibold text-gray-700">Customer proof #{index + 1}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-3 text-center text-sm text-gray-500 md:hidden">Swipe to see more customer proof →</p>
        </div>
      </section>

      <section className="bg-gray-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div data-section-view="why_checkstall_view">
              <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Why Checkstall</p>
              <h2 className="text-3xl font-black sm:text-4xl">One simpler path from China to Nigeria</h2>
              <p className="mt-3 text-lg leading-8 text-gray-600">
                Handle product discovery, supplier payment, China-side inspection and shipping in one place.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex gap-3 rounded-2xl border border-gray-100 bg-white p-4">
                  <span className="mt-1 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-white">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <p className="font-medium text-gray-800">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gray-950 p-6 text-white shadow-soft-lg md:p-10">
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary-200">WhatsApp community</p>
                <h2 className="text-3xl font-black sm:text-4xl">Still Learning About Importing From China?</h2>
                <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-300">
                  Join our WhatsApp community for importing information, product opportunities, Checkstall updates and guidance on getting started.
                </p>
              </div>
              <a
                href={WHATSAPP_COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-cta-location="community"
                className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-7 py-4 text-base font-bold text-gray-950 transition-colors hover:bg-gray-100"
              >
                Join WhatsApp Community
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-primary py-10 text-white md:py-14">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div data-section-view="final_cta_view">
            <h2 className="text-3xl font-black sm:text-5xl">Ready to Start Importing?</h2>
          </div>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-100">
            Download Checkstall Import and start searching for products from China.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/downloads"
              data-cta-location="final"
              className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-7 py-4 font-bold text-primary transition-colors hover:bg-gray-100"
            >
              Download Checkstall Import
            </Link>
            <Link
              to="/how-to-use"
              data-cta-location="final"
              className="inline-flex min-h-14 items-center justify-center rounded-2xl border-2 border-white px-7 py-4 font-bold text-white transition-colors hover:bg-white hover:text-primary"
            >
              View Step-by-Step Tutorial
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowItWorks;
